<script>
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import ActionFooter from '@components/footer/action-footer.vue';
import AdminPanel from '@components/admin-panel/admin-panel.vue';
import CardSet from '@components/cards/card-set.vue';
import CircuitForm from '@components/forms/circuit-form.vue';
import CircuitList from '@components/circuit/circuit-list.vue';
import ExerciseList from '@components/exercise/exercise-list.vue';
import HeroCircuit from '@components/hero/hero-circuit.vue';
import UseLargeScreen from '@components/use-large-screen.vue';
import Layout from '@layouts/main.vue';
import moment from 'moment';
import NoResults from '@components/notifications/no-results.vue';
import { adminPanelMixin } from '@mixins/admin-panel-mixin.js';
import { circuitModelMixin } from '@mixins/models/circuit-model-mixin';
import { breakpointsMixin } from '@mixins/breakpoints-mixin.js';
import { helperMixin } from '@mixins/helper-mixin';
import { cleverTapMixin } from '@mixins/cleverTap-mixin';
import { mixin as clickaway } from 'vue-clickaway';
import {
  adminPanelComputed,
  adminPanelMethods,
  authComputed,
  circuitComputed,
  circuitMethods,
  exerciseComputed,
  exerciseMethods,
  exerciseDifficultyMethods,
  modalMethods,
  muscleGroupMethods,
  equipmentMethods,
} from '@state/helpers';

export default {
  page: {
    title: 'Circuits',
    meta: [{ name: 'description', content: 'The Circuits page.' }],
  },
  props: {},
  components: {
    ActionFooter,
    AdminPanel,
    CardSet,
    CircuitForm,
    CircuitList,
    ExerciseList,
    HeroCircuit,
    Layout,
    NoResults,
    UseLargeScreen,
  },
  mixins: [
    adminPanelMixin,
    circuitModelMixin,
    helperMixin,
    clickaway,
    cleverTapMixin,
    breakpointsMixin,
  ],
  data() {
    return {
      footerWidth: '',
      isCircuitValid: false,
      isSubmitting: false,
      lastSaved: null,
      initCircuit: {},
      currentCircuitsList: [],
      hasChanges: false,
      selectedExerciseIndex: null,
      tempCircuitList: [],
      overActionFooter: false,
      loadingData: false,
      initCircuitlength: 0,
      initCircuitsList: [],
      createFromParent: false,
    };
  },
  computed: {
    ...adminPanelComputed,
    ...authComputed,
    ...circuitComputed,
    ...exerciseComputed,
    hasCircuits() {
      return this.allCircuits && this.allCircuits.length;
    },
    hasNewCircuit() {
      return this.newCircuit && this.newCircuit.length === 1;
    },
    hasExercises() {
      return (
        this.selectedCircuit &&
        this.selectedCircuit.exercises &&
        this.selectedCircuit.exercises.length
      );
    },
    isNewCircuit() {
      return this.selectedCircuit && this.selectedCircuit.newCircuit;
    },
    noResults() {
      return {
        title: this.isFiltering
          ? `We couldn’t find what you’re looking for.`
          : 'Choose or create your own circuit.',
        text: this.isFiltering
          ? `This circuit does not exist in the circuit library. If you want to use this circuit, you will need create it.`
          : `Select a circuit to make changes. If you can't find the circuit
              you want, you can go ahead and create it.`,
        btnText: this.isFiltering ? 'Clear Filter' : 'Search',
      };
    },
    circuits() {
      return this.currentCircuitsList;
    },
    isTrainer() {
      return (
        this.roles && this.roles.length > 0 && this.roles[0] === 'isTrainer'
      );
    },
    allowEditing() {
      return (
        this.selectedCircuit &&
        (!this.isTrainer ||
          (this.isTrainer && this.selectedCircuit.trainer_id === this.trainerId) ||
          this.newCircuit.length > 0)
      );
    },
  },
  async created() {
    try {
      this.loadingData = true;
      if (!this.allCircuits || !this.allCircuits.length) {
        await this.getCircuitShape({
          orgId: this.userOrgId,
          trainerId: this.trainerId ? this.trainerId : '0',
        });
        await this.getAllCircuits({
          orgId: this.userOrgId,
          trainerId: this.trainerId ? this.trainerId : '0',
        });
        this.loadingData = false;
        await this.init();
      }

      if (!this.allExercises || !this.allExercises.length) {
        this.getAllExerciseDifficulties({
          orgId: this.userOrgId,
          trainerId: this.trainerId ? this.trainerId : '0',
        });
        this.getAllMuscleGroups({
          orgId: this.userOrgId,
          trainerId: this.trainerId ? this.trainerId : '0',
        });
        await this.getAllExercises({
          orgId: this.userOrgId,
          trainerId: this.trainerId ? this.trainerId : '0',
        });
        await this.getAllEquipment({
          orgId: this.userOrgId,
          trainerId: this.trainerId ? this.trainerId : '0',
        });
      }
      await this.init();
      this.loadingData = false;
    } catch (e) {
      this.loadingData = false;
      console.log(e);
    }
  },
  async mounted() {
    await this.init();
    window.addEventListener('resize', this.resizeFooter);
  },
  destroyed() {
    this.$_clearSelections();
    window.removeEventListener('resize', this.resizeFooter);
  },
  methods: {
    ...adminPanelMethods,
    ...circuitMethods,
    ...exerciseDifficultyMethods,
    ...exerciseMethods,
    ...modalMethods,
    ...muscleGroupMethods,
    ...equipmentMethods,
    init() {
      if (this.allCircuits && this.allCircuits.length) {
        this.initCircuitsList = cloneDeep(this.allCircuits);
        this.initCircuitlength = this.initCircuitsList.length;
        this.currentCircuitsList = cloneDeep(this.allCircuits);
        this.currentCircuitsList = this.compare(this.currentCircuitsList);
      }
      this.resizeFooter();
    },
    resizeFooter() {
      if (this.$refs.contentWrapper) {
        this.footerWidth = this.$refs.contentWrapper.offsetWidth;
      }
    },
    async showAdminPanel(component, data = null, size = '') {
      await this.setPanelContent(this.getPanelContent(component, data, size));
      this.setShowAdminPanel();
    },
    showNewCircuitItem() {
      if (!this.newCircuit.length) {
        this.toggleNewCircuitPlaceholder(this.newCircuitPlaceholder);
        this.setSelectedCircuit(this.newCircuitPlaceholder);
        this.createNewItem = true;
      }
    },
    confirmDelete() {
      if (this.isNewCircuit) {
        this.createFromParent = false;
        if (this.allCircuits && this.allCircuits.length && !this.isFiltering) {
          this.initCircuit = null;
          this.setSelectedCircuit(null);
          this.toggleNewCircuitPlaceholder();
        }
        if (this.isFiltering) {
          this.initCircuit = null;
          this.setSelectedCircuit(null);
          this.toggleNewCircuitPlaceholder();
        }
      } else {
        this.setSelectedItem({
          ...this.selectedCircuit,
          context: 'Circuit',
        });
        this.$bvModal.show('confirmation-modal');
      }
    },
    onUpdateCircuitProps(circuit) {
      this.setSelectedCircuit(circuit);
    },
    onSelectExercise(exercise) {
      exercise.setDirections = [this.getCircuitExerciseSetModel()];
      this.setAddCircuitExercise(exercise);
      this.setHideAdminPanel();
    },
    onDeselectExercise() {
      this.setSelectedExercise(null);
    },
    findExerciseIndex() {
      const exercises = this.selectedCircuit.exercises;
      return exercises.findIndex(
        (exercise) => exercise._id === this.selectedExercise._id
      );
    },
    moveCircuitExceriseUp() {
      const index = this.selectedExerciseIndex;
      if (index > 0) {
        this.setMoveCircuitExerciseUp({
          exercise: this.selectedExercise,
          index,
        });
        this.selectedExerciseIndex--;
      }
    },
    moveCircuitExceriseDown() {
      const exercises = this.selectedCircuit.exercises;
      const index = this.selectedExerciseIndex;
      if (index < exercises.length - 1) {
        this.setMoveCircuitExerciseDown({
          exercise: this.selectedExercise,
          index,
        });
        this.selectedExerciseIndex++;
      }
    },
    dupeCircuitExcerise() {
      this.setAddCircuitExercise(this.selectedExercise);
      this.setSelectedExercise(null);
      const container = document.getElementsByClassName('content-wrapper')[0];
      container.scroll({ top: container.scrollHeight, behavior: 'smooth' });
    },
    removeCircuitExcerise() {
      const index = this.selectedExerciseIndex;
      this.setRemoveCircuitExercise(index);
      this.setSelectedExercise(null);
      this.selectedExerciseIndex = null;
    },
    buildCircuitPayload() {
      return {
        ...this.selectedCircuit,
        org_id: this.userOrgId,
        trainer_id: this.trainerId || '0',
        user_id: '0',
      };
    },
    async handleSave() {
      if(!this.isCircuitValid && this.selectedCircuit.tabata){
        this.setNotifyModalContext({
          textMain: `Error!`,
          textSecond: `The Tabata circuit type required time and rest after time should be greater than zero in every exercise!`,
        });
        this.$bvModal.show('notify-modal');
        return;
      }

      this.isSubmitting = true;
      const payload = await this.buildCircuitPayload();

      if (this.isNewCircuit) {
        this.createCircuit({ circuitObj: payload })
          .then(async (res) => {
            this.isSubmitting = false;
            this.toggleNewCircuitPlaceholder(null);
            const oldCircuitsList = cloneDeep(this.allCircuits);
            const newCircuitsList = oldCircuitsList
              .splice(this.initCircuitlength)
              .reverse();
            this.currentCircuitsList = newCircuitsList.concat(
              this.compare(oldCircuitsList)
            );
            this.initCircuit = cloneDeep({
              ...this.currentCircuitsList[0],
            });
            this.setSelectedCircuit({
              ...this.currentCircuitsList[0],
            });

            if (
              this.currentUser &&
              this.currentUser.roles &&
              this.currentUser.roles.isTrainer &&
              res &&
              res.programCircuit
            ) {
              const circuit = res.programCircuit._id;
              const trainer = this.trainerEmail;
              this.$_pushCleverTapEvent('Create Library Circuit', {
                circuit,
                trainer,
              });
            }

            this.setHasUnsavedCircuitChanges(false);
          })
          .catch((e) => console.log(e));
      } else {
        this.updateCircuit({ circuitObj: payload })
          .then(async () => {
            this.isSubmitting = false;
            if (
              this.currentCircuitsList[0]._id ===
              this.compare(cloneDeep(this.allCircuits))[0]._id
            ) {
              this.currentCircuitsList = this.compare(
                cloneDeep(this.allCircuits)
              );
            } else {
              const oldCircuitsList = cloneDeep(this.allCircuits);
              const newCircuitsList = oldCircuitsList
                .splice(this.initCircuitlength)
                .reverse();
              this.currentCircuitsList = newCircuitsList.concat(
                this.compare(oldCircuitsList)
              );
            }

            this.setHasUnsavedCircuitChanges(false);
          })
          .catch((e) => console.log(e));
      }
      this.lastSaved = `${moment().format('MMMM Do')} at ${moment().format(
        'hh:mm a'
      )}`;
      this.setInitCircuit(this.selectedCircuit);
      this.hasChanges = !isEqual(this.selectedCircuit, this.initCircuit);
    },
    setCircuit(circuit) {
      this.setSelectedCircuit(circuit);
    },
    setInitCircuit(circuit) {
      this.initCircuit = cloneDeep(circuit);
      this.selectedExerciseIndex = null;
    },
    cancelChanges() {
      this.setSelectedCircuit(this.initCircuit);
    },
    compare(circuits) {
      circuits.sort(function(a, b) {
        if (
          a.label &&
          b.label &&
          a.label.toLowerCase() < b.label.toLowerCase()
        ) {
          return -1;
        }
        if (
          a.label &&
          b.label &&
          a.label.toLowerCase() > b.label.toLowerCase()
        ) {
          return 1;
        }
        return 0;
      });
      return circuits;
    },
    onCreateFromParent() {
      this.createFromParent = true;
    },
    onStartSearch() {
      if (this.newCircuit.length) {
        this.toggleNewCircuitPlaceholder();
      }
      this.setAllCircuits(this.initAllCircuits);
      if (this.isFiltering) {
        this.toggleIsFiltering();
      }

      const inputWithLabel = document.getElementsByClassName(
        'input-icon-wrapper'
      )[0];

      if (inputWithLabel) {
        if (inputWithLabel.children.length > 1) {
          const inputElement = inputWithLabel.children[1].firstChild;
          inputElement.value = null;
          inputElement.focus();
        }
      }
    },
    clickedOutside() {
      this.createFromParent = false;
    },
    onDuplicateCircuit() {
      const payload = cloneDeep(this.selectedCircuit);
      payload.label = 'Copy'.concat(' ', payload.label);
      payload.trainer_id = this.trainerId;
      delete payload._id;

      this.createCircuit({ circuitObj: payload })
        .then(async (res) => {
          const oldCircuitsList = cloneDeep(this.allCircuits);
          const newCircuitsList = oldCircuitsList
            .splice(this.initCircuitlength)
            .reverse();
          this.currentCircuitsList = newCircuitsList.concat(
            this.compare(oldCircuitsList)
          );
          this.initCircuit = cloneDeep({
            ...this.currentCircuitsList[0],
          });
          this.setSelectedCircuit({
            ...this.currentCircuitsList[0],
          });
          if (
            this.currentUser &&
            this.currentUser.roles &&
            this.currentUser.roles.isTrainer &&
            res &&
            res.programCircuit
          ) {
            const circuit = res.programCircuit._id;
            const trainer = this.trainerEmail;
            this.$_pushCleverTapEvent('Create Library Circuit', {
              circuit,
              trainer,
            });
          }
          this.setHasUnsavedCircuitChanges(false);
        })
        .catch((e) => console.log(e));
    },
  },
  watch: {
    selectedCircuit: {
      deep: true,
      handler(newVal, oldVal) {
        if (newVal && oldVal && this.initCircuit) {
          if (newVal.newCircuit) {
            this.lastSaved = null;
          }
          this.isCircuitValid = newVal.label.length && newVal.exercises.length;
          // If circuit valid then check for additional validation
          if(this.isCircuitValid && newVal.tabata && newVal.exercises.length){
              newVal.exercises.forEach((exercise) => {
                exercise.setDirections.forEach((setDirection) => {
                  if(!setDirection.afterSetRest || !setDirection.time){
                    this.isCircuitValid =false;
                  }
                });
              });
          }
          this.hasChanges = !isEqual(this.selectedCircuit, this.initCircuit);

          this.setHasUnsavedCircuitChanges(this.hasChanges);
        }

        if (
          newVal &&
          oldVal &&
          newVal._id &&
          oldVal._id &&
          newVal._id !== oldVal._id
        ) {
          this.lastSaved = null;
        }
        if (newVal === null) {
          // to handle the case of deleting a circuit
          this.setHasUnsavedCircuitChanges(false);
        }
      },
    },
    allCircuits(n, o) {
      if (
        n &&
        n.length &&
        this.initCircuitsList &&
        this.initCircuitsList.length
      ) {
        let l = 0;
        this.initCircuitsList.forEach((target) => {
          n.forEach((origin) => {
            if (target._id === origin._id) {
              l++;
            }
          });
        });
        this.initCircuitlength = l;
      }

      if (this.isFiltering) {
        this.setInitCircuit(null);
        this.setSelectedCircuit(null);
        this.hasChanges = false;
        const oldCircuitsList = cloneDeep(this.allCircuits);
        const newCircuitsList = oldCircuitsList
          .splice(this.initCircuitlength)
          .reverse();
        this.currentCircuitsList = newCircuitsList.concat(
          this.compare(oldCircuitsList)
        );
      }
      if (
        (!this.isFiltering && this.isNewCircuit === undefined) ||
        (this.isNewCircuit === null && this.initCircuit && this.allCircuits)
      ) {
        const isUpdating = this.allCircuits.some((e) => {
          return e._id === this.initCircuit._id;
        });

        if (!isUpdating) {
          this.setInitCircuit(null);
          this.setSelectedCircuit(null);
          this.hasChanges = false;
        }
        const oldCircuitsList = cloneDeep(this.allCircuits);
        const newCircuitsList = oldCircuitsList
          .splice(this.initCircuitlength)
          .reverse();
        this.currentCircuitsList = newCircuitsList.concat(
          this.compare(oldCircuitsList)
        );
      }
      if (!this.isFiltering && this.isNewCircuit) {
        const oldCircuitsList = cloneDeep(this.allCircuits);
        const newCircuitsList = oldCircuitsList
          .splice(this.initCircuitlength)
          .reverse();
        this.currentCircuitsList = newCircuitsList.concat(
          this.compare(oldCircuitsList)
        );
      }
      if (
        !this.isFiltering &&
        this.initAllCircuits.length === this.allCircuits.length
      ) {
        const oldCircuitsList = cloneDeep(this.allCircuits);
        const newCircuitsList = oldCircuitsList
          .splice(this.initCircuitlength)
          .reverse();
        this.currentCircuitsList = newCircuitsList.concat(
          this.compare(oldCircuitsList)
        );
      }
    },
  },
};
</script>

<template>
  <UseLargeScreen v-if="isSmallScreenView" />
  <Layout v-else>
    <AdminPanel
      :title="panelContent ? panelContent.title : ''"
      :size="panelContent ? panelContent.size : ''"
    >
      <component
        v-if="panelContent"
        :is="panelContent.component"
        :content="panelContent"
        :showExerciseFilters="panelContent.showExerciseFilters"
        @select-exercise-item="onSelectExercise"
      />
    </AdminPanel>

    <div class="container-wrapper dark">
      <CircuitList
        :circuits="circuits"
        @set-init-circuit="setInitCircuit"
        :createFromParent="createFromParent"
        @select-circuit-item="setCircuit"
        @set-create-from-parent="createFromParent = $event"
      />

      <div class="content-wrapper no-pad">
        <HeroCircuit
          v-if="(selectedCircuit && hasCircuits) || hasNewCircuit"
          :circuit="selectedCircuit"
          @update-circuit-props="onUpdateCircuitProps"
          :allowEditing="allowEditing"
        />

        <div
          :class="[
            'content-section-wrapper',
            {
              empty:
                (!hasCircuits && !hasNewCircuit) ||
                (!selectedCircuit && !loadingData),
            },
          ]"
          ref="contentWrapper"
        >
          <div
            class="create-circuit"
            v-if="
              (!hasCircuits && !hasNewCircuit && !loadingData) ||
                (!selectedCircuit && !loadingData)
            "
          >
            <BaseButton
              type="button"
              class="btn-primary btn-round"
              @click="onCreateFromParent"
              v-on-clickaway="clickedOutside"
            >
              <img
                class="invert-color"
                src="@assets/images/icon-stopwatch.svg"
              />
              Create New
            </BaseButton>
          </div>
          <div
            class="loading-wrapper"
            v-if="!hasCircuits && !hasNewCircuit && loadingData"
          >
            <BaseLoadingSpinner />
          </div>

          <div
            v-if="
              (!hasCircuits && !hasNewCircuit && !loadingData) ||
                (!selectedCircuit && !loadingData)
            "
            class="empty-state-container"
          >
            <h2>{{ noResults.title }}</h2>
            <p>{{ noResults.text }}</p>
            <div class="actions">
              <BaseButton
                v-if="true"
                type="button"
                class="btn-secondary btn-round"
                @click="onStartSearch"
              >
                {{ noResults.btnText }}
              </BaseButton>
              <BaseButton
                v-if="true"
                type="button"
                class="btn-primary btn-round"
                @click="onCreateFromParent"
                v-on-clickaway="clickedOutside"
              >
                <img
                  class="invert-color"
                  src="@assets/images/icon-stopwatch.svg"
                />
                Create New
              </BaseButton>
            </div>
          </div>

          <span v-if="(hasCircuits || hasNewCircuit) && selectedCircuit">
            <span v-if="hasExercises">
              <CardSet
                v-for="(exercise, i) in selectedCircuit.exercises"
                :key="i"
                :exercise="exercise"
                :exerciseIndex="i"
                :circuit="selectedCircuit"
                @set-selected-exercise-index="selectedExerciseIndex = $event"
                :selectedExerciseIndex="selectedExerciseIndex"
                :overActionFooter="overActionFooter"
                :allowEditing="allowEditing"
              />
            </span>
            <BaseButton
              type="button"
              class="btn-secondary btn-round btn-add-more"
              v-if="hasExercises && allowEditing"
              @click="showAdminPanel('ExerciseList', null, 'lg')"
            >
              <BaseIcon name="plus" /> Add Another Exercise
            </BaseButton>
            <div
              class="add-new-exercise-wrapper"
              v-if="!hasExercises && allowEditing"
            >
              <BaseButton
                type="button"
                class="btn-secondary btn-round btn-add-more"
                @click="showAdminPanel('ExerciseList', null, 'lg')"
              >
                <BaseIcon name="plus" /> Add First Exercise
              </BaseButton>
            </div>
          </span>

          <ActionFooter
            v-if="
              ((allCircuits && allCircuits.length > 0) || isNewCircuit) &&
                ((selectedCircuit && !selectedExercise) ||
                  (selectedCircuit && selectedExerciseIndex === null))
            "
            :style="`width: ${footerWidth}px;`"
            :selected-item="selectedExercise"
            item-type="Exercise"
            color="black"
          >
            <span
              v-if="!hasChanges && lastSaved && !isSubmitting && allowEditing"
              class="saved-status"
              slot="actionState"
            >
              Last Saved: {{ lastSaved }}
            </span>
            <span
              v-if="!hasChanges && !lastSaved && !isNewCircuit && allowEditing"
              class="saved-status"
              slot="actionState"
              >No changes</span
            >
            <span
              v-if="hasChanges && !isNewCircuit && allowEditing"
              slot="secondaryAction"
            >
              Not saved
              <span class="link-deselect" @click="cancelChanges">
                Cancel
              </span>
            </span>
            <span v-if="!allowEditing" class="saved-status" slot="actionState"
              >Duplicate to make changes</span
            >
            <BaseButton
              v-if="isTrainer"
              type="button"
              class="btn-primary btn-clear"
              @click="onDuplicateCircuit"
            >
              <svg width="22" height="26" viewBox="0 0 22 26" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:class="'icon-trash'" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path fill-rule="evenodd" clip-rule="evenodd" d="M15.876.4H2.296C1.053.4.034 1.43.034 2.69v16.037h2.264V2.691h13.579V.4zm3.394 4.582H6.823c-1.245 0-2.263 1.03-2.263 2.29V23.31c0 1.26 1.018 2.291 2.263 2.291H19.27c1.245 0 2.264-1.03 2.264-2.29V7.272c0-1.26-1.019-2.291-2.264-2.291zM6.823 23.309H19.27V7.273H6.823v16.036z" fill="#2F70DE"/></svg>
              <span>Duplicate</span>
            </BaseButton>
            <span v-if="allowEditing" class="divider-vertical">&nbsp;</span>
            <BaseButton
              v-if="allowEditing"
              type="button"
              class="btn-primary btn-clear"
              @click="confirmDelete"
            >
              <svg width="14" height="18" viewBox="0 0 14 18" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:class="'icon-trash'" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path fill-rule="evenodd" clip-rule="evenodd" d="M10.5 1H14v2H0V1h3.5l1-1h5l1 1zM3 18c-1.1 0-2-.9-2-2V4h12v12c0 1.1-.9 2-2 2H3z" fill="#000"/></svg>
              <span v-if="isNewCircuit">Cancel</span>
              <span v-else>Delete</span>
            </BaseButton>
            <span v-if="allowEditing" class="divider-vertical">&nbsp;</span>
            <BaseButton
              v-if="allowEditing"
              :style="{ opacity: !hasChanges || isSubmitting ? 0.5 : 1 }"
              type="button"
              class="btn-primary btn-clear"
              @click="handleSave"
              :disabled="isSubmitting || !hasChanges"
            >
              <BaseIcon v-if="isSubmitting" name="sync" spin />
              <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg" v-else="" v-bind:class="'icon-save'" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path fill-rule="evenodd" clip-rule="evenodd" d="M13.222 0H1.89C.84 0 0 .85 0 1.889V15.11C0 16.15.84 17 1.889 17H15.11c1.04 0 1.89-.85 1.89-1.889V3.778L13.222 0zM8.5 15.111a2.83 2.83 0 01-2.833-2.833A2.83 2.83 0 018.5 9.444a2.83 2.83 0 012.833 2.834A2.83 2.83 0 018.5 15.11zM1.889 5.667h9.444V1.889H1.89v3.778z" fill="#fff"/></svg>
              <span v-if="isNewCircuit">Add</span>
              <span v-else>Save</span> Circuit
            </BaseButton>
          </ActionFooter>

          <ActionFooter
            v-if="selectedExercise && selectedExerciseIndex !== null"
            :style="`width: ${footerWidth}px; padding-left:0;`"
            :selected-item="selectedExercise"
            item-type="Exercise"
          >
            <span slot="secondaryAction">
              Not Saved
            </span>
            <span slot="secondaryAction">
              <span
                class="action-title link-deselect"
                @click="onDeselectExercise"
              >
                Deselect
              </span>
            </span>
            <span
              @mouseover="() => (overActionFooter = true)"
              @mouseleave="() => (overActionFooter = false)"
            >
              <BaseButton
                type="button"
                class="btn-primary btn-clear"
                @click="moveCircuitExceriseUp"
              >
                <svg width="13" height="15" viewBox="0 0 13 15" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:class="'icon-arrow'" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M1.178 7.019l5.196-4.935 5.196 4.935" stroke="#fff" stroke-width="2"/><path d="M6.44 3.916v10" stroke="#fff" stroke-width="2" stroke-linecap="square"/></svg>
                Move Up
              </BaseButton>
              <span class="divider-vertical">&nbsp;</span>
              <BaseButton
                type="button"
                class="btn-primary btn-clear"
                @click="moveCircuitExceriseDown"
              >
                <svg width="13" height="15" viewBox="0 0 13 15" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:class="'icon-arrow'" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M1.609 7.981l5.195 4.935L12 7.981" stroke="#fff" stroke-width="2"/><path d="M6.871 11.084v-10" stroke="#fff" stroke-width="2" stroke-linecap="square"/></svg>
                Move Down
              </BaseButton>
              <span class="divider-vertical">&nbsp;</span>
              <BaseButton
                type="button"
                class="btn-primary btn-clear"
                @click="removeCircuitExcerise"
              >
                <svg width="14" height="18" viewBox="0 0 14 18" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:class="'icon-trash'" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path fill-rule="evenodd" clip-rule="evenodd" d="M10.5 1H14v2H0V1h3.5l1-1h5l1 1zM3 18c-1.1 0-2-.9-2-2V4h12v12c0 1.1-.9 2-2 2H3z" fill="#000"/></svg>
                Delete
              </BaseButton>
              <span class="divider-vertical">&nbsp;</span>
              <BaseButton
                type="button"
                class="btn-primary btn-clear"
                @click="dupeCircuitExcerise"
              >
                <svg width="22" height="26" viewBox="0 0 22 26" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:class="'icon-trash'" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path fill-rule="evenodd" clip-rule="evenodd" d="M15.876.4H2.296C1.053.4.034 1.43.034 2.69v16.037h2.264V2.691h13.579V.4zm3.394 4.582H6.823c-1.245 0-2.263 1.03-2.263 2.29V23.31c0 1.26 1.018 2.291 2.263 2.291H19.27c1.245 0 2.264-1.03 2.264-2.29V7.272c0-1.26-1.019-2.291-2.264-2.291zM6.823 23.309H19.27V7.273H6.823v16.036z" fill="#2F70DE"/></svg>
                Duplicate
              </BaseButton>
            </span>
          </ActionFooter>
        </div>
      </div>
    </div>
  </Layout>
</template>

<style lang="scss" scoped>
@import '@design';

// stylelint-disable selector-pseudo-element-no-unknown
.empty-state-container {
  background: $color-white;
  border-radius: 12px;
  box-shadow: 0 4px 8px rgba($color-black-wo-rgb, 0.08);
  display: flex;
  flex-direction: column;
  margin-bottom: 30vh;
  margin-left: auto;
  margin-right: auto;
  margin-top: 35vh;
  padding: 6rem;
  text-align: center;
  width: 70%;
  p {
    margin-top: 2rem;
  }
  .actions {
    align-items: center;
    display: flex;
    justify-content: center;
    margin-top: 2rem;
    ::v-deep .button {
      height: 3.7rem;
      padding-bottom: 0.2rem;
      padding-top: 0.2rem;
      white-space: nowrap;
    }
    ::v-deep .button.btn-secondary {
      margin-right: 10px;
    }
  }
}
.loading-wrapper {
  align-items: center;
  display: flex;
  justify-content: center;
  margin-top: 12vh;
  width: 100%;
  ::v-deep .loading {
    height: 4rem;
    width: 4rem;
  }
}

.content-section-wrapper {
  padding: 1.6rem 3.2rem 16rem;

  &.empty {
    align-items: center;
    display: flex;
    height: 100%;
    justify-content: center;
  }
}

.create-circuit {
  position: absolute;
  right: 3%;
  top: 13%;
}
.add-new-exercise-wrapper {
  display: flex;
  justify-content: center;
}

.btn-add-more {
  margin-bottom: 24px;
  padding: 0.8rem 2rem;
  svg {
    path {
      fill: red;
    }
  }
}

.no-results .icon-curl {
  path {
    fill: white;
  }
}
</style>
